/**
 * Line-style icon set — single source of truth for all icons in the app.
 * All icons inherit color via `currentColor` and accept a `size` prop (default 18).
 * Stroke is consistent (1.75) so icons feel like a unified family.
 */

const baseProps = (size = 18) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.75,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
});

// ---------- WhatsApp ----------

export function WhatsAppIcon({ size = 18 }) {
  // Brand glyph — filled (currentColor), not stroked, since the chat bubble is mostly silhouette.
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.464 3.488"/>
    </svg>
  );
}

// ---------- AI / Sparkles ----------

export function SparklesIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z" />
      <path d="M19 15l.7 2.1L22 18l-2.3.9L19 21l-.7-2.1L16 18l2.3-.9L19 15z" />
      <path d="M5 14l.5 1.5L7 16l-1.5.5L5 18l-.5-1.5L3 16l1.5-.5L5 14z" />
    </svg>
  );
}

// ---------- Order types ----------

export function UtensilsIcon({ size = 18 }) {
  // Fork + knife — Dine in
  return (
    <svg {...baseProps(size)}>
      <path d="M7 2v8a2 2 0 0 0 2 2h0a2 2 0 0 0 2-2V2" />
      <line x1="9" y1="12" x2="9" y2="22" />
      <path d="M16 2c-1.5 0-3 2-3 5s1.5 5 3 5v10" />
    </svg>
  );
}

export function BagIcon({ size = 18 }) {
  // Paper bag — Takeaway
  return (
    <svg {...baseProps(size)}>
      <path d="M5 8h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2Z" />
      <path d="M9 8V6a3 3 0 0 1 6 0v2" />
    </svg>
  );
}

export function ScooterIcon({ size = 18 }) {
  // Moped — Delivery
  return (
    <svg {...baseProps(size)}>
      <circle cx="6" cy="17" r="3" />
      <circle cx="18" cy="17" r="3" />
      <path d="M9 17h6" />
      <path d="M14 6h3l1 11" />
      <path d="M9 17l3-7h4" />
      <path d="M5 11h4" />
    </svg>
  );
}

// ---------- Cart / Commerce ----------

export function CartIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <circle cx="9" cy="20" r="1.5" />
      <circle cx="18" cy="20" r="1.5" />
      <path d="M2 3h3l2.5 12.3a1.5 1.5 0 0 0 1.5 1.2h8.4a1.5 1.5 0 0 0 1.5-1.2L21 6H6" />
    </svg>
  );
}

// ---------- Status ----------

export function CheckIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)} strokeWidth={2}>
      <polyline points="4 12 10 18 20 6" />
    </svg>
  );
}

export function AlertIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

export function LightbulbIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M9 18h6" />
      <path d="M10 22h4" />
      <path d="M15.09 14a5 5 0 1 0-6.18 0c.55.6 1 1.2 1.09 2v.01h4V16c.09-.8.54-1.4 1.09-2Z" />
    </svg>
  );
}

// ---------- Contact ----------

export function PhoneIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.35 1.85.59 2.81.72a2 2 0 0 1 1.72 2Z" />
    </svg>
  );
}

export function ChatIcon({ size = 18 }) {
  // WhatsApp / message bubble
  return (
    <svg {...baseProps(size)}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5Z" />
    </svg>
  );
}

export function MailIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
      <polyline points="22,6 12,13 2,6" />
    </svg>
  );
}

export function GlobeIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
      <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
  );
}

export function MapPinIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <circle cx="12" cy="10" r="3" />
    </svg>
  );
}

// ---------- People / table ----------

export function UserIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  );
}

export function ChairIcon({ size = 18 }) {
  // A simple chair / table icon
  return (
    <svg {...baseProps(size)}>
      <path d="M6 19v3" />
      <path d="M18 19v3" />
      <path d="M4 11V5a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v6" />
      <path d="M3 11h18v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3z" />
    </svg>
  );
}

// ---------- Time / metrics ----------

export function ClockIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}

export function FlameIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.07-1.96-.4-4.5 2.5-6 .25 1.5.75 2.75 2 4 1.25 1.25 2 2.6 2 4.25a6.25 6.25 0 1 1-12.5 0c0-1.62.69-2.94 1.5-4 .81 1.06 1.5 2.38 1.5 4z" />
    </svg>
  );
}

// ---------- Social ----------

export function InstagramIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <rect x="3" y="3" width="18" height="18" rx="5" ry="5" />
      <path d="M16 11.37a4 4 0 1 1-7.91 1.36 4 4 0 0 1 7.91-1.36z" />
      <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" />
    </svg>
  );
}

export function FacebookIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
    </svg>
  );
}

export function TwitterIcon({ size = 18 }) {
  // X / Twitter
  return (
    <svg {...baseProps(size)}>
      <path d="M18 4h3l-7.5 8.6L22 22h-6.7l-5.2-6.2L4 22H1l8-9.2L1.5 4H8.4l4.7 5.7L18 4z" />
    </svg>
  );
}

export function TikTokIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
    </svg>
  );
}

// ---------- Generic helpers ----------

export function PlusIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

export function MinusIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  );
}

export function CloseIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="6" y1="18" x2="18" y2="6" />
    </svg>
  );
}

export function StarIcon({ size = 18, filled = false }) {
  return (
    <svg {...baseProps(size)} fill={filled ? 'currentColor' : 'none'}>
      <polygon points="12 2 15.1 8.6 22 9.3 17 14.1 18.2 21 12 17.5 5.8 21 7 14.1 2 9.3 8.9 8.6 12 2" />
    </svg>
  );
}

export function HeartIcon({ size = 18, filled = false }) {
  return (
    <svg {...baseProps(size)} fill={filled ? 'currentColor' : 'none'}>
      <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
    </svg>
  );
}

export function PencilIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  );
}

export function TrashIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

export function CopyIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
  );
}

export function ClipboardIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" ry="1" />
    </svg>
  );
}

export function CircleDotIcon({ size = 18, filled = false }) {
  return (
    <svg {...baseProps(size)}>
      <circle cx="12" cy="12" r="9" fill={filled ? 'currentColor' : 'none'} />
    </svg>
  );
}

export function DotIcon({ size = 8 }) {
  // Tiny solid dot used as a status indicator inside pills
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
      <circle cx="12" cy="12" r="6" />
    </svg>
  );
}

export function SearchIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

export function PrinterIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );
}

export function ChevronDownIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

export function ChevronUpIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <polyline points="18 15 12 9 6 15" />
    </svg>
  );
}

export function ChevronRightIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <polyline points="9 6 15 12 9 18" />
    </svg>
  );
}

export function UploadIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

export function DownloadIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}

export function RefreshIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10" />
      <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14" />
    </svg>
  );
}

export function ExternalLinkIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

export function SettingsIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

export function BellIcon({ size = 18 }) {
  return (
    <svg {...baseProps(size)}>
      <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}
